import { FC, useCallback, useState } from 'react';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { GridList } from '../../../common/grid/GridList';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import { hasRole, isExternal } from '../../../../utils/roles';
import { useAppSelector } from '../../../../store';
import { selectIdentity } from '../../../../store/entities/identity';
import { ActivityCertificateModal } from './ActivityCertificateModal';
import { InnerTooltipWrapper, QcsTooltip } from '../../../common/basic/QcsTooltip';
import { SectionHeader } from '../../../common/SectionHeader';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { ActivityAddModal } from './ActivityAddModal';
import { ActivityDeleteModal } from './ActivityDeleteModal';
import { FaIcon } from '../../../common/FaIcon';
import { faCircleExclamation, faRoadBarrier } from '@fortawesome/free-solid-svg-icons';
import { SxProps, Theme, useTheme } from '@mui/material';
import { ActivityRiskModal } from './ActivityRiskModal';
import { getLangNameObj } from '../../../../utils/format';
import { useInvitation } from './invitationFunctions';
import { ActivityRequestModal } from './ActivityRequestModal';
import { InvitationActivityDto } from '@api/models/invitation/InvitationActivityDto';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitationState } from '@api/models/enums/InvitationState';
import { InvitationDocsExportType } from '@api/models/enums/InvitationDocsExportType';
import { invitationService } from '@api/services/services';
import { resources } from '@api/resources';
import { downloadBlob } from '../../../../utils/download';

export const InvTabActivities: FC = () => {
  const { t, i18n, invitation } = useInvitation();

  const theme = useTheme();
  const identity = useAppSelector(selectIdentity);
  const [riskModal, setRiskModal] = useState<InvitationActivityDto>();
  const [certificateModal, setCertificateModal] = useState<InvitationActivityDto>();
  const [checkedItems, setCheckedItems] = useState<InvitationActivityDto[]>([]);
  const [activityToDelete, setActivityToDelete] = useState<InvitationActivityDto>();
  const [activityAddModal, setActivityAddModal] = useState<boolean>(false);
  const [activityRequestModal, setActivityRequestModal] = useState<boolean>(false);

  const hasNoAssignedPerson = useCallback(
    (item: InvitationActivityDto) =>
      (invitation.persons ?? []).length === 0 || !invitation.persons!.some((person) => person.activities.some((a) => a.id === item.activity?.id)),
    [invitation.persons],
  );

  const handleGetRowSx = useCallback(
    (item: InvitationActivityDto): SxProps<Theme> | undefined => {
      if (hasNoAssignedPerson(item)) {
        return { boxShadow: `inset 4px 0 0 0 ${theme.palette.warning.main}` };
      }
      return undefined;
    },
    [hasNoAssignedPerson, theme],
  );

  const getRequiredAction = (item: InvitationActivityDto) => {
    if (isExternal(identity) && item.activity?.riskRequired && !item.document) {
      return t('invitation.activity.requiredActionUploadRisks');
    }
    if (!isExternal(identity) && item.approvalRequired && item.activity?.riskRequired && !!item.document && !item.approvedOn) {
      return t('invitation.activity.requiredActionApproveRisks');
    }

    return '';
  };

  const handleRenderData = (item: InvitationActivityDto) => {
    const showRiskDisabled = !item.activity?.riskRequired;
    const showRiskOk =
      !showRiskDisabled && !!item.document && (!hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace]) || !!item.approvedOn);

    const deleteDisabled =
      invitation.state === InvitationState.Archived ||
      !hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace]) ||
      //At least one activity must left.
      (invitation.activities ?? []).length < 2 ||
      //After delete no person with no activity.
      invitation.persons?.some(
        (person) => person.activities?.some((activity) => activity.id === item.activity?.id) === true && person.activities.length === 1,
      ) === true;

    return (
      <>
        <QcsTableCell>
          {getLangNameObj(i18n, item.activity)}
          {hasNoAssignedPerson(item) && (
            <QcsTooltip title={t('invitation.activity.noWorkersAssigned')} placement="top">
              <InnerTooltipWrapper>
                <FaIcon icon={faCircleExclamation} color={theme.palette.warning.main} style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }} />
              </InnerTooltipWrapper>
            </QcsTooltip>
          )}
        </QcsTableCell>
        <QcsTableCell>{getRequiredAction(item)}</QcsTableCell>
        <QcsTableCell align="right">
          <QcsTooltip title={t('invitation.tabs.tooltips.showRisk')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton onClick={handleShowRisk(item)} resourceLoading={[resources.approveActivities]} disabled={showRiskDisabled}>
                <FaIcon
                  icon={faRoadBarrier}
                  color={showRiskDisabled ? undefined : showRiskOk ? theme.palette.success.main : theme.palette.warning.main}
                />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>

          {/* DELETE WORKPLACE */}
          <QcsTooltip title={t('invitation.activity.deleteActivity')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton onClick={handleDeleteActivity(item)} resourceLoading={[resources.approveActivities]} disabled={deleteDisabled}>
                <DeleteForeverIcon color={deleteDisabled ? undefined : 'error'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
        </QcsTableCell>
      </>
    );
  };

  const handleChangeChecked = (items: InvitationActivityDto[]) => {
    setCheckedItems(items);
  };

  const handleItemToSelectObject = (item: InvitationActivityDto) => item;

  const handleSelectObjectGetId = (item: InvitationActivityDto) => item.id!;

  const handleCheckDisabled = (item: InvitationActivityDto) => !item.activity?.riskRequired || !item.document;

  const handleShowRisk = (item: InvitationActivityDto) => (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setRiskModal(item);
  };

  const handleCloseRisk = () => {
    setRiskModal(undefined);
  };

  const handleCloseCertificate = () => {
    setCertificateModal(undefined);
  };

  const handleDeleteActivity = (item: InvitationActivityDto) => (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setActivityToDelete(item);
  };

  const handleCloseDeleteActivity = () => {
    setActivityToDelete(undefined);
  };

  const handleAddActivity = () => {
    setActivityAddModal(true);
  };

  const handleCloseAddActivity = () => {
    setActivityAddModal(false);
  };

  const handleDownloadRisks = async () => {
    const res = await invitationService.generateZipFile(
      invitation.id!,
      checkedItems.map((x) => x.id!),
      InvitationDocsExportType.ActivityRisks,
    );
    await downloadBlob(res);
  };

  const showAddButton = hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace]);

  const showRequestButton = hasRole(identity.roles, [UserRole.ExternalManager]);

  return (
    <>
      <ActivityCertificateModal item={certificateModal} onClose={handleCloseCertificate} />
      <ActivityDeleteModal activity={activityToDelete} onClose={handleCloseDeleteActivity} />
      <ActivityAddModal open={activityAddModal} onClose={handleCloseAddActivity} />
      <ActivityRequestModal open={activityRequestModal} onClose={() => setActivityRequestModal(false)} />
      <ActivityRiskModal itemId={riskModal?.id} onClose={handleCloseRisk} />

      {(showAddButton || showRequestButton) && (
        <SectionHeader
          title=" "
          addText={showAddButton ? 'activity.add' : 'activity.requestAdd'}
          handleAddClick={showAddButton ? handleAddActivity : () => setActivityRequestModal(true)}
          addDisabled={invitation.state === InvitationState.Archived}
        />
      )}

      <GridList<InvitationActivityDto, InvitationActivityDto>
        headers={[{ captionStr: 'invitation.activity.name' }, { captionStr: 'invitation.activity.requiredAction' }, {}]}
        data={invitation.activities ?? []}
        renderData={handleRenderData}
        getRowSx={handleGetRowSx}
        search={false}
        hidePagination={true}
        checkbox={true}
        checkedItems={checkedItems}
        changeChecked={handleChangeChecked}
        itemToSelectObject={handleItemToSelectObject}
        selectObjectGetId={handleSelectObjectGetId}
        isCheckDisabled={handleCheckDisabled}
      />
      {hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist]) && (
        <QcsLoadingButton
          variant="contained"
          onClick={handleDownloadRisks}
          disabled={checkedItems.length === 0}
          resourceLoading={resources.generateZipFile}
          sx={{ mt: '1.5rem' }}
        >
          {t('invitation.workplace.download')}
        </QcsLoadingButton>
      )}
    </>
  );
};
